import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { EditPageLayout } from '@/components/shared/EditPageLayout';
import { FormSection } from '@/components/shared/FormSection';
import type { DeviceModel } from '@packages/shared-types';
import {
  getDeviceModelById,
  type UpdateDeviceModelPayload,
  updateDeviceModel,
  deleteDeviceModel,
} from '@/services/deviceModelService';

const DEVICE_FEATURES = [
  { key: 'rfid', labelKey: 'pages.deviceModels.form.rfid' },
  { key: 'barcode', labelKey: 'pages.deviceModels.form.barcode' },
  { key: 'radar', labelKey: 'pages.deviceModels.form.radar' },
  { key: 'battery', labelKey: 'pages.deviceModels.form.battery' },
  { key: 'temperature', labelKey: 'pages.deviceModels.form.temperature' },
  { key: 'powerLevel', labelKey: 'pages.deviceModels.form.powerLevel' },
  { key: 'antennaStatus', labelKey: 'pages.deviceModels.form.antennaStatus' },
  { key: 'connectionStatus', labelKey: 'pages.deviceModels.form.connectionStatus' },
] as const;

const deviceModelFormSchema = z.object({
  name: z.string().min(1, 'pages.deviceModels.form.nameRequired'),
  manufacturer: z.string().optional(),
  slug: z
    .string()
    .min(1, 'pages.deviceModels.form.slugRequired')
    .regex(/^[a-z0-9-]+$/, 'pages.deviceModels.form.slugHelper'),
  rfid: z.boolean().optional(),
  barcode: z.boolean().optional(),
  radar: z.boolean().optional(),
  battery: z.boolean().optional(),
  temperature: z.boolean().optional(),
  powerLevel: z.boolean().optional(),
  antennaStatus: z.boolean().optional(),
  connectionStatus: z.boolean().optional(),
});

type DeviceModelFormValues = z.infer<typeof deviceModelFormSchema>;

function getFeaturesFromForm(values: DeviceModelFormValues): Record<string, boolean> {
  const features: Record<string, boolean> = {};
  for (const { key } of DEVICE_FEATURES) {
    const val = values[key as keyof DeviceModelFormValues];
    features[key] = val === true;
  }
  return features;
}

export function EditDeviceModelPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const editTitle = t('pages.deviceModels.edit.title');
  const editDescription = t('pages.deviceModels.edit.description');
  const deleteDialogTitle = t('pages.deviceModels.dialogs.delete.title');
  const successUpdate = t('pages.deviceModels.success.update');
  const successDelete = t('pages.deviceModels.success.delete');
  const errorLoad = t('pages.deviceModels.error.load');
  const errorUpdate = t('pages.deviceModels.error.update');
  const errorDelete = t('pages.deviceModels.error.delete');
  const errorNotFound = t('pages.deviceModels.error.notFound');

  const [deviceModel, setDeviceModel] = useState<DeviceModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<DeviceModelFormValues>({
    resolver: zodResolver(deviceModelFormSchema),
    defaultValues: {
      name: '',
      manufacturer: '',
      slug: '',
      rfid: false,
      barcode: false,
      radar: false,
      battery: false,
      temperature: false,
      powerLevel: false,
      antennaStatus: false,
      connectionStatus: false,
    },
  });

  const formatErrorMessage = useCallback(
    (message?: string): string | null => {
      if (!message) return null;
      return t(message);
    },
    [t],
  );

  const loadDeviceModel = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getDeviceModelById(id);
      setDeviceModel(data);
      const features = data.features || {};
      reset({
        name: data.name,
        manufacturer: data.manufacturer || '',
        slug: data.slug,
        rfid: features.rfid === true,
        barcode: features.barcode === true,
        radar: features.radar === true,
        battery: features.battery === true,
        temperature: features.temperature === true,
        powerLevel: features.powerLevel === true,
        antennaStatus: features.antennaStatus === true,
        connectionStatus: features.connectionStatus === true,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : errorLoad);
    } finally {
      setIsLoading(false);
    }
  }, [id, reset, errorLoad]);

  useEffect(() => {
    void loadDeviceModel();
  }, [loadDeviceModel]);

  const handleSubmitForm = async (values: DeviceModelFormValues): Promise<void> => {
    if (!id) return;
    setIsSaving(true);
    setError(null);
    const payload: UpdateDeviceModelPayload = {
      name: values.name,
      manufacturer: values.manufacturer || undefined,
      slug: values.slug,
      features: getFeaturesFromForm(values),
    };
    try {
      await updateDeviceModel(id, payload);
      await loadDeviceModel();
      toast.success(successUpdate);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : errorUpdate;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!id || !deviceModel) return;
    setIsDeleting(true);
    try {
      await deleteDeviceModel(id);
      toast.success(successDelete);
      navigate('/device-models');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : errorDelete;
      setError(errorMessage);
      toast.error(errorMessage);
      setIsDeleting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    void handleSubmit(handleSubmitForm)();
  };

  if (!id) {
    return (
      <div className="p-6 text-sm text-destructive">{errorNotFound}</div>
    );
  }

  return (
    <EditPageLayout
      title={editTitle}
      description={deviceModel ? editDescription : undefined}
      isLoading={isLoading}
      error={error}
      formId="device-model-edit-form"
      onSubmit={handleFormSubmit}
      onCancel={() => navigate(-1)}
      cancelLabel={t('pages.deviceModels.actions.cancel')}
      onSave={() => void handleSubmit(handleSubmitForm)()}
      saveLabel={t('pages.deviceModels.actions.update')}
      savingLabel={t('pages.deviceModels.actions.updating')}
      isSaving={isSaving}
      onDelete={handleDelete}
      deleteLabel={t('pages.deviceModels.actions.delete')}
      deletingLabel={t('pages.deviceModels.actions.deleting')}
      isDeleting={isDeleting}
      deleteDialogTitle={deleteDialogTitle}
      deleteDialogDescription={t('pages.deviceModels.dialogs.delete.description', {
        name: deviceModel?.name,
      })}
      entityExists={!!deviceModel}
    >
      <form
        id="device-model-edit-form"
        className="space-y-6"
        onSubmit={handleSubmit(handleSubmitForm)}
      >
        <FormSection
          title={t('pages.deviceModels.form.name')}
          error={
            errors.name
              ? formatErrorMessage(errors.name.message) ??
                (errors.name.message as string)
              : null
          }
        >
          <Input
            id="device-model-name"
            {...register('name')}
            placeholder={t('pages.deviceModels.form.name')}
            disabled={isSaving}
          />
        </FormSection>
        <FormSection title={t('pages.deviceModels.form.manufacturer')}>
          <Input
            id="device-model-manufacturer"
            {...register('manufacturer')}
            placeholder={t('pages.deviceModels.form.manufacturer')}
            disabled={isSaving}
          />
        </FormSection>
        <FormSection
          title={t('pages.deviceModels.form.slug')}
          description={t('pages.deviceModels.form.slugHelper')}
          error={
            errors.slug
              ? formatErrorMessage(errors.slug.message) ??
                (errors.slug.message as string)
              : null
          }
        >
          <Input
            id="device-model-slug"
            {...register('slug')}
            placeholder="chainway-c5"
            disabled={isSaving}
          />
        </FormSection>
        <FormSection title={t('pages.deviceModels.form.features')}>
          <div className="grid grid-cols-2 gap-4 pt-2">
            {DEVICE_FEATURES.map(({ key, labelKey }) => (
              <Controller
                key={key}
                name={key}
                control={control}
                render={({ field }) => (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`device-model-${key}`}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isSaving}
                    />
                    <Label
                      htmlFor={`device-model-${key}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {t(labelKey)}
                    </Label>
                  </div>
                )}
              />
            ))}
          </div>
        </FormSection>
      </form>
    </EditPageLayout>
  );
}
